import { useState } from "react";
import { Clock, Plus, Trash2, Save, AlertCircle, Video } from "lucide-react";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ONLINE = { id: "virtual", name: "Online" };
const isOnlineSlot = (s) => s.location_id === "virtual" || (!s.location_id && s.mode === "virtual");
const slotLoc = (s) => (isOnlineSlot(s) ? "virtual" : s.location_id || "");

const SlotRow = ({ s, i, onUpd, onRm, isOnline, locations }) => (
  <div className="flex items-center gap-3 eyw-card p-4 flex-wrap" data-testid={`loc-hours-row-${i}`}>
    <Clock size={16} className="text-[#144F75]" />
    <select value={s.day} onChange={(e) => onUpd(i, { day: parseInt(e.target.value, 10) })} className="eyw-input" data-testid={`loc-hours-day-${i}`}>
      {DAYS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}
    </select>
    <input type="time" value={s.start} onChange={(e) => onUpd(i, { start: e.target.value })} className="eyw-input" data-testid={`loc-hours-start-${i}`} />
    <span className="text-[#4A5D6B]">to</span>
    <input type="time" value={s.end} onChange={(e) => onUpd(i, { end: e.target.value })} className="eyw-input" data-testid={`loc-hours-end-${i}`} />
    {locations && (
      <select value="" onChange={(e) => e.target.value && onUpd(i, e.target.value === "virtual" ? { location_id: "virtual", mode: "virtual" } : { location_id: e.target.value, mode: s.mode === "both" ? "both" : "physical" })}
        className="eyw-input" data-testid={`loc-hours-assign-${i}`}>
        <option value="">Move to a location…</option>
        {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    )}
    {!isOnline && !locations && (
      <label className="text-xs text-[#4A5D6B] flex items-center gap-1.5">
        <input type="checkbox" checked={s.mode === "both" || s.mode === "hybrid"} onChange={(e) => onUpd(i, { mode: e.target.checked ? "both" : "physical" })}
          className="accent-[#144F75]" data-testid={`loc-hours-also-online-${i}`} /> Also online
      </label>
    )}
    <button onClick={() => onRm(i)} className="text-red-500 ml-auto" data-testid={`loc-hours-remove-${i}`} aria-label="Remove hours"><Trash2 size={16} /></button>
  </div>
);

export const LocationHoursEditor = ({ slots, onChange, onSave, locations = [] }) => {
  const all = [...locations.map((l) => ({ id: l.id, name: l.name + (l.type === "own" ? " (my space)" : "") })), ONLINE];
  const hasLegacy = slots.some((s) => slotLoc(s) === "");
  const [picked, setSel] = useState(null);
  const sel = picked === null || (picked === "" && !hasLegacy) ? (hasLegacy ? "" : all[0].id) : picked;
  const count = (id) => slots.filter((s) => slotLoc(s) === id || (id === "virtual" && ["both", "hybrid"].includes(s.mode))).length;
  const upd = (i, patch) => onChange(slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const rm = (i) => onChange(slots.filter((_, idx) => idx !== i));
  const add = () => onChange([...slots, { day: 1, start: "09:00", end: "12:00", location_id: sel, mode: sel === "virtual" ? "virtual" : "physical" }]);
  const rows = slots.map((s, i) => [s, i]).filter(([s]) => slotLoc(s) === sel);
  const tabs = [...(hasLegacy ? [{ id: "", name: "Older hours (no location)" }] : []), ...all];

  return (
    <div data-testid="location-hours-editor">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-2">
        <h2 className="font-serif text-2xl text-[#1A242B]">Hours at each location</h2>
        <div className="flex gap-2">
          {sel !== "" && <button onClick={add} data-testid="enh-add-slot" className="eyw-btn-ghost !py-2 text-sm"><Plus size={16} /> Add hours</button>}
          <button onClick={onSave} data-testid="enh-save-availability" className="eyw-btn !py-2 text-sm"><Save size={16} /> Save</button>
        </div>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-5 max-w-2xl">Each center and Online keeps its own weekly hours. Members can only book you at a location once you've set hours there.</p>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="loc-hours-tabs">
        {tabs.map((t) => {
          const n = t.id === "" ? slots.filter((s) => slotLoc(s) === "").length : count(t.id);
          return (
            <button key={t.id || "legacy"} onClick={() => setSel(t.id)} data-testid={`loc-hours-tab-${t.id || "legacy"}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${sel === t.id ? "bg-[#144F75] text-white border-[#144F75]" : "bg-white text-[#1A242B] border-[#144F75]/15 hover:border-[#144F75]/40"}`}>
              {t.id === "virtual" && <Video size={14} />}{t.name}
              {n ? <span className="text-xs opacity-75">{n}</span> : <AlertCircle size={14} className={sel === t.id ? "text-white" : "text-[#D98E73]"} />}
            </button>
          );
        })}
      </div>
      {sel === "" && (
        <p className="text-sm bg-[#F5E6E0] border border-[#D98E73]/40 rounded-xl p-3 mb-4 max-w-2xl" data-testid="loc-hours-legacy-note">
          These hours were saved before each location had its own. They still apply to your services' original location. Move each one to a location to use it elsewhere.
        </p>
      )}
      <div className="space-y-3 max-w-3xl">
        {rows.length === 0 && <p className="text-[#4A5D6B] text-sm" data-testid="loc-hours-empty">No hours here yet — members can't book you at this location.</p>}
        {rows.map(([s, i]) => <SlotRow key={i} s={s} i={i} onUpd={upd} onRm={rm} isOnline={sel === "virtual"} locations={sel === "" ? all : null} />)}
        {sel === "virtual" && slots.some((s) => ["both", "hybrid"].includes(s.mode)) && (
          <p className="text-xs text-[#5C8A72]" data-testid="loc-hours-online-shared-note">
            Plus {slots.filter((s) => ["both", "hybrid"].includes(s.mode)).length} center hour block(s) marked "Also online" — edit those on their center's tab.
          </p>
        )}
        {sel !== "virtual" && sel !== "" && slots.some((s) => slotLoc(s) === sel && ["both", "hybrid"].includes(s.mode)) && (
          <p className="text-xs text-[#5C8A72]">Hours marked "Also online" count for Online too.</p>
        )}
      </div>
    </div>
  );
};
